import { useState, useEffect } from "react";
import { connect } from "react-redux";
import { RootState } from "../../state/configureStore";
import StandardModal from "./StandardModal";
import ModalSize from "./ModalSize";
import { TextField, Typography } from "@material-ui/core";
import FirebaseService from "../../services/firebase/FirebaseService";
import { ProviderContext, withSnackbar, SnackbarMessage, OptionsObject, SnackbarKey } from "notistack";
import LoadingButton from "../Loading/LoadingButton";
import { GENERAL_MAX_LENGTH } from "../../utils/stringFormatting";
import { useHistory } from "react-router-dom";

import "./JoinClassroomModal.css";

interface JoinClassroomModalProps {
  uid: string;
  closeModal: () => void;
  open: boolean;
  classCode?: string;
}

const joinClassroom =
  (
    classCode: string,
    uid: string,
    setButtonState: (state: LoadingButton) => void,
    enqueueSnackbar: (message: SnackbarMessage, options?: OptionsObject) => SnackbarKey,
    closeModal: () => void,
    redirectHome: () => void,
  ) =>
  async () => {
    const result = await FirebaseService.checkClassCode(classCode);
    if (!result.exists) {
      enqueueSnackbar("This class code does not exist. Double check the code you have entered.", {
        variant: "error",
        autoHideDuration: 5000,
      });
      setButtonState(LoadingButton.BeforeLoading);
      return;
    }

    const alreadyInClass = await FirebaseService.checkStudentOnClass(uid, result.id);
    if (alreadyInClass) {
      enqueueSnackbar(`You are already a part of ${result.className}.`, {
        variant: "warning",
        autoHideDuration: 5000,
      });
      setButtonState(LoadingButton.BeforeLoading);
      return;
    }

    await FirebaseService.manageStudentOnClass(uid, result.id, true);
    closeModal();
    enqueueSnackbar(`You have joined ${result.className}!`, {
      variant: "success",
      autoHideDuration: 5000,
    });
    setButtonState(LoadingButton.DoneLoading);
    redirectHome();
  };

const JoinClassroomModal = ({
  open,
  closeModal,
  uid,
  classCode = "",
  enqueueSnackbar,
}: JoinClassroomModalProps & ProviderContext) => {
  const [{ classCode: internalClassCode, error }, setState] = useState({ classCode, error: "" });
  const [loadingButtonState, setButtonState] = useState(LoadingButton.BeforeLoading);
  const history = useHistory();
  const redirectHome = () => history.push("/");

  useEffect(() => {
    const doIt = async () => {
      if (!classCode) return;
      const result = await FirebaseService.checkClassCode(classCode);
      if (!result.exists) {
        setState({
          classCode,
          error: "Invalid Class Code",
        });
      }
    };
    doIt();
  }, [classCode]);

  return (
    <StandardModal
      title="Join Classroom"
      size={ModalSize.medium}
      onlyOneButton={false}
      open={open}
      cancelClose={closeModal}
      onSubmit={joinClassroom(internalClassCode, uid, setButtonState, enqueueSnackbar, closeModal, redirectHome)}
      loadingButtonState={loadingButtonState}
      changeLoadingButtonState={buttonState => setButtonState(buttonState)}
    >
      <div className="mainBody">
        <Typography className="helperText" variant="subtitle1">
          Ask your teacher for the class code, then enter it here.
        </Typography>
        <TextField
          error={Boolean(error)}
          helperText={error}
          label="Enter Class Code"
          variant="outlined"
          onChange={e =>
            setState({
              classCode: e.target.value,
              error: "",
            })
          }
          inputProps={{ maxLength: GENERAL_MAX_LENGTH }}
          value={internalClassCode}
        />
      </div>
    </StandardModal>
  );
};

const mapStateToProps = (state: RootState): { uid: string } => ({
  uid: state.currentUserState.currentUser?.uid || "",
});

export default connect(mapStateToProps)(withSnackbar(JoinClassroomModal));
